import {useAuthService} from "~/composables/useAuthService";
import {computed} from "vue";
import {useRouter, useRoute} from "vue-router";
import {useMailer} from "~/composables/useMailer";

export function useNavigationItems() {
  const {
    isAuthenticated,
    user,
    logout,
    stopImpersonating,
    isImpersonating,
    isSuperUser,
    unreadCount
  } = useAuthService();
  const {stringifyQuery} = useCrudConverters();
  const router = useRouter();
  const route = useRoute();

  const adminBreadcrumbs = useState('adminBreadcrumbs', () => []);

  const convertToLocalRoute = (link) => {
    const config = useRuntimeConfig();
    const baseUrl = config.public.baseUrl;
    if(link.startsWith(baseUrl)) {
      return link.replace(baseUrl, '');
    }
    return null;
  }

  const setAdminBreadcrumbs = (items) => {
    adminBreadcrumbs.value = items;
  };

  const superUserItems = computed(() => {
    const items = [];
    items.push({
      icon: 'mdi-view-dashboard',
      title: 'Dashboard',
      link: `/admin/super`,
      active: false
    });
    items.push({
      icon: 'mdi-account',
      title: 'Users',
      link: `/admin/super`,
      active: false
    });
    items.push({
      icon: 'mdi-account-group',
      title: 'Clubs',
      link: `/admin/super`,
      active: false
    });
    items.push({
      icon: 'mdi-newspaper-variant-outline',
      title: 'Blog',
      link: `/admin/super/blog`,
      active: route.path.includes('/admin/super/blog')
    });
    items.push({
      icon: 'mdi-help-box',
      title: 'Help',
      link: `/admin/super`,
      active: false
    });
    items.push({
      icon: 'mdi-road-variant',
      title: 'Roadmap',
      link: `/admin/super/roadmap`,
      active: route.path.includes('/admin/super/roadmap')
    });
    items.push({
      icon: 'mdi-basketball',
      title: 'Sports',
      link: `/admin/super/sports`,
      active: route.path.includes('/admin/super/sports')
    });
    items.push({
      icon: 'mdi-star-box',
      title: 'Testimonials',
      link: `/admin/super/testimonials`,
      active: route.path.includes('/admin/super/testimonials')
    });
    items.push({
      icon: 'mdi-send',
      title: 'Mass Mailer',
      link: `/admin/super`,
      active: false
    });
    return items;
  });
 const { filtersToQuery } = useMailer();
  const adminMenuItems = computed(() => {
    const items = [];
    if (isAuthenticated.value) {
      if (route.params.clubId
      ) {
        if (unreadCount.value > 0 && (route.path.includes('/admin/clubs/' + route.params.clubId + '/chat') || route.path.includes('/chat'))) {
          items.push({
            title: 'Chat',
            link: `/admin/clubs/${route.params.clubId}/chat`,
            icon: 'mdi-chat',
            active: route.path.includes('/admin/clubs/' + route.params.clubId + '/chat'),
            iconColor: 'success',
            badge: unreadCount.value
          });
        } else {
          items.push({
            title: 'Chat',
            link: `/admin/clubs/${route.params.clubId}/chat`,
            icon: 'mdi-chat',
            active : route.path.includes('/admin/clubs/' + route.params.clubId + '/chat'),
            iconColor: 'success'
          });
        }
        items.push({
          icon: 'mdi-account-group',
          title: 'Members',
          link: `/admin/clubs/${route.params.clubId}/members`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/members')
        });
        items.push({
          icon: 'mdi-table',
          title: 'Leagues',
          link: `/admin/clubs/${route.params.clubId}/leagues`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/leagues')
        });
        items.push({
          icon: 'mdi-ladder',
          title: 'Ladders',
          link: `/admin/clubs/${route.params.clubId}/ladders`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/ladders')
        });
        items.push({
          icon: 'mdi-scoreboard',
          title: 'Scoring Systems',
          link: `/admin/clubs/${route.params.clubId}/scoringSystems`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/scoringSystems')
        });
        items.push({
          icon: 'mdi-send',
          title: 'Email Members',
          appendIcon: "mdi-new-box",
          appendIconColor: "primary",
          link: {
            ...route,
            query: {
              ...route.query,
              mailerQuery: filtersToQuery({
                clubIds: [route.params.clubId]
              })
            }
          },
          active: false
        });
      }

    }
    return items;
  });

  const adminLowerMenuItems = computed(() => {
    const items = [];
    if(!isSuperUser) {
      items.push({
        icon: 'mdi-help-circle-outline',
        title: 'Help',
        link: '/help'
      });
      items.push({
        icon: 'mdi-email-edit-outline',
        title: 'Contact Support',
        link: '/contact'
      });
    }
    if(isSuperUser || (isAuthenticated.value && user.adminFor && user.adminFor.length > 0)) {
      items.push({
        title: 'Switch club',
        link: '/admin?switchClub=true',
        icon: 'mdi-swap-horizontal'
      });
    }
    items.push({
      title: 'Exit Admin',
      link: '/',
      icon: 'mdi-exit-to-app'
    });
    return items;
  })

  const mainMenuItems = computed(() => {
    const items = [];
    if (isAuthenticated.value) {
      if (unreadCount.value > 0) {
        items.push({title: 'Chat', link: '/chat', icon: 'mdi-chat', iconColor: 'success', badge: unreadCount.value});
      } else {
        items.push({title: 'Chat', link: '/chat', icon: 'mdi-chat', iconColor: 'success'});
      }
    }
    items.push(...[
      {title: 'Home', link: '/'},
      {title: 'Clubs', link: '/clubs'}
    ]);
    if (isSuperUser.value) {
      items.push(...[
          {title: 'Roadmap', link: '/roadmap'},
          {title: 'Blog', link: '/blog'}
        ]);
    }
    items.push(...[
        {title: 'About', link: '/about'},
        {title: 'Help', link: '/help'},
        {title: 'Contact', link: '/contact'},
      ]);
    if (!isAuthenticated.value) {
      items.push({
        title: 'Sign in',
        link: '/login' + stringifyQuery({redirectUrl: route.path}, "?"),
        icon: 'mdi-lock'
      });
      items.push({title: 'Sign up', link: '/register', icon: 'mdi-pencil'});
    }
    return items;
  });
  const userTopRightItems = computed(() => {
    let items = [
      {icon: 'mdi-account', text: 'Your profile', route: 'YourProfile'},
    ];
    items.push({icon: 'mdi-logout', text: 'Sign out', action: 'logout'});
    if (isImpersonating.value) {
      items.push({icon: 'mdi-cancel', text: 'Stop impersonating', action: 'stopImpersonating'});
    }
    return items;
  });
  const onUserTopRightItemClick = async (item) => {
    if (item.link) {
      router.push(item.link).catch(() => {
      });
    } else if (item.action) {
      if (item.action === 'logout') {
        await router.push({name: 'Home'}).then(async () => {
          await logout();
        });
      } else if (item.action === 'stopImpersonating') {
        await stopImpersonating();
      }
    } else if (item.route) {
      router.push({name: item.route}).catch(() => {
      });
    }
  };
  return {
    convertToLocalRoute,
    adminBreadcrumbs,
    setAdminBreadcrumbs,
    userTopRightItems,
    onUserTopRightItemClick,
    mainMenuItems,
    adminMenuItems,
    adminLowerMenuItems,
    superUserItems
  };
}

